import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useApiClient } from "../../../api";
import {
  FruitifyContainer,
  RegistrationCard,
} from "../../../shared-components/Containers";
import FruitifyLogo from "../../../shared-components/FruitifyLogo";
import { MARKETING_UNSUBSCRIBE_REASONS } from "../../account/components/MarketingEmailPreferences";

export const MarketingUnsubscribe = () => {
  const { uid, token } = useParams();
  const ApiClient = useApiClient();
  const [status, setStatus] = useState("loading");
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    ApiClient.users
      .getMarketingUnsubscribeTokenStatus({ uid, token })
      .then((result) => active && setStatus(result))
      .catch(() => active && setStatus("error"));
    return () => {
      active = false;
    };
  }, [ApiClient.users, uid, token]);

  const unsubscribe = async () => {
    setSubmitting(true);
    setError("");
    try {
      const result = await ApiClient.users.unsubscribeFromMarketingLink({
        uid,
        token,
        reason,
        detail,
      });
      setStatus(result.success ? "unsubscribed" : result.status);
    } catch (_error) {
      setError("We could not update your preference. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const statusContent = {
    expired: "This unsubscribe link has expired.",
    invalid: "This unsubscribe link is invalid.",
    error: "We could not check this unsubscribe link. Please try again.",
  };

  return (
    <FruitifyContainer>
      <RegistrationCard variant="outlined">
        <Stack spacing={3} sx={{ width: "100%" }}>
          <Stack direction="row" justifyContent="center">
            <FruitifyLogo />
          </Stack>
          <Typography variant="h3">Marketing email preferences</Typography>
          {status === "loading" && (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Checking your unsubscribe link...</Typography>
            </Stack>
          )}
          {statusContent[status] && (
            <Alert severity={status === "error" ? "error" : "warning"}>
              {statusContent[status]}
            </Alert>
          )}
          {status === "unsubscribed" && (
            <Alert severity="success">
              You have been unsubscribed from Fruitify marketing emails.
            </Alert>
          )}
          {status === "valid" && (
            <>
              <Typography color="text.secondary">
                Confirm that you would like to stop receiving onboarding and
                other marketing emails.
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="public-marketing-unsubscribe-reason-label">
                  Reason (optional)
                </InputLabel>
                <Select
                  labelId="public-marketing-unsubscribe-reason-label"
                  value={reason}
                  label="Reason (optional)"
                  onChange={(event) => setReason(event.target.value)}
                >
                  <MenuItem value="">Prefer not to say</MenuItem>
                  {MARKETING_UNSUBSCRIBE_REASONS.map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Anything else you would like us to know? (optional)"
                value={detail}
                onChange={(event) => setDetail(event.target.value)}
                multiline
                minRows={3}
                slotProps={{ htmlInput: { maxLength: 1000 } }}
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button
                variant="contained"
                onClick={unsubscribe}
                disabled={submitting}
                sx={{ alignSelf: "flex-start" }}
              >
                {submitting ? "Unsubscribing..." : "Confirm unsubscribe"}
              </Button>
            </>
          )}
        </Stack>
      </RegistrationCard>
    </FruitifyContainer>
  );
};
